import { com } from '@eidu/form'
import FieldData from './FieldData'
import EqualityHashMap from '../../util/EqualityHashMap'
import ParsingValidationError = com.eidu.sharedlib.entity.validation.ParsingValidationError
import ValidationError = com.eidu.sharedlib.entity.validation.ValidationError
import Success = com.eidu.sharedlib.util.Success
import Failure = com.eidu.sharedlib.util.Failure
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import TextValue = com.eidu.sharedlib.entity.field.TextValue
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import NumberValue = com.eidu.sharedlib.entity.field.NumberValue
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue
import MissingValueValidationError = com.eidu.sharedlib.entity.validation.MissingValueEntityValidationError
import Outcome = com.eidu.sharedlib.util.Outcome
import DateValue = com.eidu.sharedlib.entity.field.DateValue
import localDateFromString = com.eidu.kotlinJsInterop.localDateFromString

export type FieldErrors = ReadonlyMap<FieldId, readonly ValidationError[]>
export type BatchFieldErrors = readonly FieldErrors[]

export const fieldValueFromString = (value: string, type: FieldType): Outcome<ValidationError, FieldValue> => {
  switch (type) {
    case FieldType.Text:
      return new Success(new TextValue(value))
    case FieldType.Number: {
      const number = Number(value)
      if (value.trim().length === 0 || Number.isNaN(number))
        return new Failure(new ParsingValidationError(`Invalid number: '${value}'`))
      else return new Success(new NumberValue(number))
    }
    case FieldType.Reference: {
      if (value.length === 0) return new Success(null)
      const id = entityIdFromStringOrNull(value)
      if (!id) return new Failure(new ParsingValidationError(`Invalid entity id: '${value}'`))
      else return new Success(new ReferenceValue(id))
    }
    case FieldType.Date:
      try {
        return new Success(new DateValue(localDateFromString(value)))
      } catch (_e) {
        return new Failure(new ParsingValidationError(`Invalid date value '${value}'`))
      }
    default:
      return new Failure(new ParsingValidationError(`Unsupported field type: ${type}`))
  }
}

export const fieldValuesFromStrings = (
  values: readonly string[],
  fields: readonly FieldData[]
): Outcome<FieldErrors, Map<FieldId, FieldValue>> => {
  if (values.length !== fields.length) throw Error('Number of values mismatch')

  const fieldValues = values.map((value, index) => {
    const { field } = fields[index]
    if (field.required && value.trim().length === 0)
      return new Failure(new MissingValueValidationError(`Value required for field ${field.name}`))
    else return fieldValueFromString(value, field.type)
  })
  if (fieldValues.some((it) => it instanceof Failure)) {
    return new Failure(
      new EqualityHashMap<FieldId, readonly ValidationError[]>(
        fieldValues
          .map(
            (it, index) =>
              [fields[index].field.id, it instanceof Failure ? [it.value as ValidationError] : []] as [
                FieldId,
                readonly ValidationError[],
              ]
          )
          .filter(([, e]) => e.length > 0)
      )
    )
  } else {
    return new Success(
      new EqualityHashMap(
        fieldValues.map((valueOutcome, index) => [fields[index].field.id, (valueOutcome as Success<FieldValue>).value])
      )
    )
  }
}

export const fieldValuesFromStringsBatch = (
  values: readonly (readonly string[])[],
  fields: readonly FieldData[]
): Outcome<BatchFieldErrors, readonly Map<FieldId, FieldValue>[]> => {
  const results = values.map((row) => fieldValuesFromStrings(row, fields))

  if (results.some((it) => it instanceof Failure)) {
    const noErrors: FieldErrors = new EqualityHashMap()
    return new Failure(results.map((it) => (it instanceof Failure ? it.value : noErrors)))
  } else {
    return new Success(results.map((it) => (it as Success<Map<FieldId, FieldValue>>).value))
  }
}
